import React, { useMemo } from 'react';
import {
  Sun,
  Moon,
  Home,
  Users,
  Settings,
  Wallet,
  Bell,
  Search,
  ChevronDown,
  Plus,
  ArrowRight,
  Check,
  X,
} from 'lucide-react';

type LayoutType = 'SIDEBAR' | 'TOPBAR' | 'MINIMAL' | 'DASHBOARD_FIRST';
type ButtonVariant = 'solid' | 'outline' | 'ghost' | 'gradient' | 'glass';
type CardVariant = 'flat' | 'elevated' | 'bordered' | 'glass';
type TableVariant = 'striped' | 'bordered' | 'minimal' | 'modern';

interface ColorScale {
  '50': string;
  '100': string;
  '200': string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  '700': string;
  '800': string;
  '900': string;
  '950': string;
}

interface ThemeConfig {
  primaryColors?: ColorScale;
  accentColors?: ColorScale;
  surfaceColors?: ColorScale;
  layoutType?: LayoutType;
  buttonVariant?: ButtonVariant;
  cardVariant?: CardVariant;
  tableVariant?: TableVariant;
  fontFamily?: string;
  borderRadius?: string;
}

interface ThemePreviewEngineProps {
  theme: ThemeConfig;
  mode?: 'light' | 'dark';
  compareWith?: ThemeConfig;
  showComparison?: boolean;
  className?: string;
}

function generateCSSVariables(theme: ThemeConfig, prefix: string = ''): Record<string, string> {
  const vars: Record<string, string> = {};
  
  if (theme.primaryColors) {
    Object.entries(theme.primaryColors).forEach(([shade, value]) => {
      vars[`--${prefix}primary-${shade}`] = value;
    });
  }
  
  if (theme.accentColors) {
    Object.entries(theme.accentColors).forEach(([shade, value]) => {
      vars[`--${prefix}accent-${shade}`] = value;
    });
  }
  
  if (theme.surfaceColors) {
    Object.entries(theme.surfaceColors).forEach(([shade, value]) => {
      vars[`--${prefix}surface-${shade}`] = value;
    });
  }
  
  if (theme.fontFamily) {
    vars[`--${prefix}font-family`] = theme.fontFamily;
  }
  
  if (theme.borderRadius) {
    vars[`--${prefix}border-radius`] = theme.borderRadius;
  }
  
  return vars;
}

function PreviewSidebar({ colors }: { colors: ColorScale }) {
  return (
    <div 
      className="w-16 h-full flex flex-col items-center py-3 gap-3"
      style={{ backgroundColor: `rgb(${colors['900']})` }}
    >
      <div 
        className="w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `rgb(${colors['700']})` }}
      >
        <Home className="w-4 h-4 text-white" />
      </div>
      <div 
        className="w-8 h-8 rounded-lg flex items-center justify-center opacity-60"
        style={{ backgroundColor: `rgb(${colors['800']})` }}
      >
        <Users className="w-4 h-4 text-white" />
      </div>
      <div 
        className="w-8 h-8 rounded-lg flex items-center justify-center opacity-60"
        style={{ backgroundColor: `rgb(${colors['800']})` }}
      >
        <Wallet className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1" />
      <div 
        className="w-8 h-8 rounded-lg flex items-center justify-center opacity-60"
        style={{ backgroundColor: `rgb(${colors['800']})` }}
      >
        <Settings className="w-4 h-4 text-white" />
      </div>
    </div>
  );
}

function PreviewTopbar({ colors, primary }: { colors: ColorScale; primary: ColorScale }) {
  return (
    <div 
      className="h-12 w-full flex items-center px-4 gap-4"
      style={{ backgroundColor: `rgb(${colors['900']})` }}
    >
      <div 
        className="w-6 h-6 rounded"
        style={{ backgroundColor: `rgb(${primary['500']})` }}
      />
      <div className="flex gap-3 flex-1">
        {['Início', 'Contas', 'PIX'].map((item, i) => (
          <span 
            key={item}
            className="text-xs"
            style={{ color: i === 0 ? 'white' : `rgb(${colors['400']})` }}
          >
            {item}
          </span>
        ))}
      </div>
      <Bell className="w-4 h-4" style={{ color: `rgb(${colors['400']})` }} />
    </div>
  );
}

function PreviewButton({ 
  variant, 
  primary, 
  label = 'Botão',
  size = 'md',
}: { 
  variant: ButtonVariant; 
  primary: ColorScale;
  label?: string;
  size?: 'sm' | 'md';
}) {
  const baseClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-xs';
  
  const styles: Record<ButtonVariant, React.CSSProperties> = {
    solid: {
      backgroundColor: `rgb(${primary['500']})`,
      color: 'white',
    },
    outline: {
      backgroundColor: 'transparent',
      border: `1px solid rgb(${primary['500']})`,
      color: `rgb(${primary['500']})`,
    },
    ghost: {
      backgroundColor: 'transparent',
      color: `rgb(${primary['500']})`,
    },
    gradient: {
      background: `linear-gradient(135deg, rgb(${primary['500']}), rgb(${primary['600']}))`,
      color: 'white',
    },
    glass: {
      backgroundColor: `rgba(${primary['500'].replace(/ /g, ', ')}, 0.2)`,
      backdropFilter: 'blur(4px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      color: 'white',
    },
  };

  return (
    <button 
      className={`${baseClasses} rounded-md font-medium`}
      style={styles[variant]}
    >
      {label}
    </button>
  );
}

function PreviewCard({
  variant,
  surface,
  children,
}: {
  variant: CardVariant;
  surface: ColorScale;
  children: React.ReactNode;
}) {
  const styles: Record<CardVariant, React.CSSProperties> = {
    flat: {
      backgroundColor: `rgb(${surface['800']})`,
    },
    elevated: {
      backgroundColor: `rgb(${surface['800']})`,
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    bordered: {
      backgroundColor: `rgb(${surface['800']})`,
      border: `1px solid rgb(${surface['700']})`,
    },
    glass: {
      backgroundColor: `rgba(${surface['800'].replace(/ /g, ', ')}, 0.5)`,
      backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
  };

  return (
    <div className="rounded-lg p-3" style={styles[variant]}>
      {children}
    </div>
  );
}

function PreviewTable({
  variant,
  surface,
}: {
  variant: TableVariant;
  surface: ColorScale;
}) {
  const rows = [
    { name: 'Item 1', value: 'R$ 100,00' },
    { name: 'Item 2', value: 'R$ 250,00' },
  ];

  const getRowStyle = (index: number): React.CSSProperties => {
    switch (variant) {
      case 'striped':
        return {
          backgroundColor: index % 2 === 0 ? `rgb(${surface['800']})` : `rgb(${surface['900']})`,
        };
      case 'bordered':
        return {
          backgroundColor: `rgb(${surface['800']})`,
          borderBottom: `1px solid rgb(${surface['700']})`,
        };
      case 'minimal':
        return {
          backgroundColor: 'transparent',
          borderBottom: `1px solid rgb(${surface['700']})`,
        };
      case 'modern':
        return {
          backgroundColor: `rgb(${surface['800']})`,
          marginBottom: '4px',
          borderRadius: '4px',
        };
      default:
        return {};
    }
  };

  return (
    <div className="text-xs">
      {rows.map((row, index) => (
        <div 
          key={index}
          className="flex justify-between px-2 py-1.5"
          style={getRowStyle(index)}
        >
          <span style={{ color: `rgb(${surface['300']})` }}>{row.name}</span>
          <span className="text-white">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

function ThemePreview({ theme, mode }: { theme: ThemeConfig; mode: 'light' | 'dark' }) {
  const primary = theme.primaryColors || {
    '50': '236 254 255', '100': '207 250 254', '200': '165 243 252',
    '300': '103 232 249', '400': '34 211 238', '500': '6 182 212',
    '600': '8 145 178', '700': '14 116 144', '800': '21 94 117',
    '900': '22 78 99', '950': '8 51 68',
  };
  
  const accent = theme.accentColors || primary;
  
  const surface = theme.surfaceColors || {
    '50': '248 250 252', '100': '241 245 249', '200': '226 232 240',
    '300': '203 213 225', '400': '148 163 184', '500': '100 116 139',
    '600': '71 85 105', '700': '51 65 85', '800': '30 41 59',
    '900': '15 23 42', '950': '2 6 23',
  };

  const layout = theme.layoutType || 'SIDEBAR';
  const buttonVariant = theme.buttonVariant || 'solid';
  const cardVariant = theme.cardVariant || 'elevated';
  const tableVariant = theme.tableVariant || 'striped';

  return (
    <div 
      className="rounded-lg overflow-hidden border border-surface-600"
      style={{ 
        backgroundColor: `rgb(${surface['900']})`,
        fontFamily: theme.fontFamily || 'Inter',
      }}
    >
      {/* Header */}
      <div 
        className="h-8 flex items-center justify-between px-3 border-b"
        style={{ 
          backgroundColor: `rgb(${surface['800']})`,
          borderColor: `rgb(${surface['700']})`,
        }}
      >
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
            <div className="w-2 h-2 rounded-full bg-green-500" />
          </div>
          <span className="text-xs" style={{ color: `rgb(${surface['400']})` }}>
            Preview
          </span>
        </div>
        <div className="flex items-center gap-2">
          {mode === 'dark' ? (
            <Moon className="w-3 h-3" style={{ color: `rgb(${surface['400']})` }} />
          ) : (
            <Sun className="w-3 h-3" style={{ color: `rgb(${surface['400']})` }} />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex h-48">
        {/* Sidebar (if applicable) */}
        {layout === 'SIDEBAR' && <PreviewSidebar colors={surface} />}

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Topbar (if applicable) */}
          {layout === 'TOPBAR' && <PreviewTopbar colors={surface} primary={primary} />}

          {/* Content Area */}
          <div className="flex-1 p-3 space-y-3 overflow-hidden">
            {/* Cards */}
            <div className="grid grid-cols-2 gap-2">
              <PreviewCard variant={cardVariant} surface={surface}>
                <div className="text-xs text-white font-medium mb-1">Saldo</div>
                <div className="text-sm font-bold" style={{ color: `rgb(${primary['400']})` }}>
                  R$ 1.234,56
                </div>
              </PreviewCard>
              <PreviewCard variant={cardVariant} surface={surface}>
                <div className="text-xs text-white font-medium mb-1">Ações</div>
                <div className="flex gap-1">
                  <PreviewButton variant={buttonVariant} primary={primary} label="PIX" size="sm" />
                  <PreviewButton variant={buttonVariant} primary={primary} label="TED" size="sm" />
                </div>
              </PreviewCard>
            </div>

            {/* Table */}
            <PreviewCard variant={cardVariant} surface={surface}>
              <div className="text-xs text-white font-medium mb-2">Transações</div>
              <PreviewTable variant={tableVariant} surface={surface} />
            </PreviewCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ThemePreviewEngine({
  theme,
  mode = 'dark',
  compareWith,
  showComparison = false,
  className = '',
}: ThemePreviewEngineProps) {
  const cssVars = useMemo(() => generateCSSVariables(theme), [theme]);
  const compareCssVars = useMemo(
    () => (compareWith ? generateCSSVariables(compareWith, 'compare-') : {}),
    [compareWith]
  );

  return (
    <div className={`space-y-4 ${className}`} style={{ ...cssVars, ...compareCssVars }}>
      {showComparison && compareWith ? (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-surface-400 mb-2">Tema Atual</div>
            <ThemePreview theme={theme} mode={mode} />
          </div>
          <div>
            <div className="text-sm text-surface-400 mb-2">Comparação</div>
            <ThemePreview theme={compareWith} mode={mode} />
          </div>
        </div>
      ) : (
        <ThemePreview theme={theme} mode={mode} />
      )}

      {/* Component Showcase */}
      <div className="bg-surface-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-white mb-3">Componentes</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Buttons */}
          <div>
            <div className="text-xs text-surface-400 mb-2">Botões</div>
            <div className="flex flex-wrap gap-2">
              <PreviewButton 
                variant={theme.buttonVariant || 'solid'} 
                primary={theme.primaryColors || { '500': '6 182 212', '600': '8 145 178' } as ColorScale}
                label="Primário"
              />
              <PreviewButton 
                variant="outline" 
                primary={theme.primaryColors || { '500': '6 182 212', '600': '8 145 178' } as ColorScale}
                label="Outline"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <div className="text-xs text-surface-400 mb-2">Status</div>
            <div className="flex gap-2">
              <span className="flex items-center gap-1 text-xs text-green-400">
                <Check className="w-3 h-3" /> Sucesso
              </span>
              <span className="flex items-center gap-1 text-xs text-red-400">
                <X className="w-3 h-3" /> Erro
              </span>
            </div>
          </div>

          {/* Input */}
          <div>
            <div className="text-xs text-surface-400 mb-2">Input</div>
            <div 
              className="flex items-center gap-2 px-2 py-1 rounded text-xs"
              style={{ 
                backgroundColor: `rgb(${theme.surfaceColors?.['700'] || '51 65 85'})`,
                border: `1px solid rgb(${theme.surfaceColors?.['600'] || '71 85 105'})`,
              }}
            >
              <Search className="w-3 h-3 text-surface-400" />
              <span className="text-surface-400">Buscar...</span>
            </div>
          </div>

          {/* Dropdown */}
          <div>
            <div className="text-xs text-surface-400 mb-2">Dropdown</div>
            <div 
              className="flex items-center justify-between gap-2 px-2 py-1 rounded text-xs"
              style={{ 
                backgroundColor: `rgb(${theme.surfaceColors?.['700'] || '51 65 85'})`,
                border: `1px solid rgb(${theme.surfaceColors?.['600'] || '71 85 105'})`,
              }}
            >
              <span className="text-white">Opção</span>
              <ChevronDown className="w-3 h-3 text-surface-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThemePreviewEngine;
